/**
 * Reveals content with an eased, staggered fade and rise as it scrolls into view.
 *
 * Items are the outermost matches of ITEM_SELECTOR inside each page region; a region
 * with no matches is revealed as a whole. Only items that start below the fold are
 * hidden, so the first screen and its LCP element are never hidden by this module
 * (the first screen's entrance animation is CSS-only, in base.css). Without this
 * module, in the theme editor, and with reduced motion, everything stays visible.
 */

const REGION_SELECTOR =
  'main .shopify-section, .shopify-section-group-footer-group, main .shopify-policy__container';
const ITEM_SELECTOR = [
  '.text-block',
  '.button:not(.button-unstyled)',
  '.image-block',
  '.icon-block',
  '.card',
  '.resource-list__item',
  '.product-grid__item',
  'product-card',
  'media-gallery',
  '.contact-form',
  '.business-contact',
  '.menu',
  '.email-signup',
  '.utilities > *',
  '.page-content rte-formatter > *',
  '.shopify-policy__title',
  '.shopify-policy__body .rte > *',
].join(',');
/** Content that manages its own visibility or positioning. */
const EXCLUDED_ANCESTOR_SELECTOR = 'dialog, [popover], sticky-add-to-cart, [hidden]';
const ATTRIBUTE = 'data-scroll-reveal';
const DELAY_PROPERTY = '--scroll-reveal-delay';
/** Body class set in layout/theme.liquid that enables the CSS first-screen entrance. */
const LOAD_CLASS = 'reveal-on-load';
const LOAD_ENTRANCE_MS = 1500;
const STAGGER_MS = 70;
const MAX_STAGGER_STEPS = 6;
/** Marks the item done if `transitionend` never fires, e.g. in a background tab. */
const CLEANUP_DELAY_MS = 1200;

/**
 * @param {Element} region
 * @returns {HTMLElement[]} The outermost reveal items in the region, or the region itself.
 */
function getItems(region) {
  /** @type {HTMLElement[]} */
  const items = [];

  for (const candidate of region.querySelectorAll(ITEM_SELECTOR)) {
    if (!(candidate instanceof HTMLElement)) continue;
    if (candidate.closest(EXCLUDED_ANCESTOR_SELECTOR)) continue;
    // Disclosure content is revealed by its own open animation.
    if (candidate.parentElement?.closest('details')) continue;
    if (candidate.parentElement?.closest(ITEM_SELECTOR)) continue;
    items.push(candidate);
  }

  if (items.length === 0 && region instanceof HTMLElement) items.push(region);
  return items;
}

/**
 * Plays the reveal transition, then marks the item done so no transform or transition is
 * left behind to affect hover effects or sticky and fixed descendants. The attribute stays
 * so the CSS first-screen entrance does not replay on the item.
 * @param {HTMLElement} item
 * @param {number} step - Position in the current stagger.
 */
function reveal(item, step) {
  const delay = Math.min(step, MAX_STAGGER_STEPS) * STAGGER_MS;
  const controller = new AbortController();
  const finish = () => {
    controller.abort();
    clearTimeout(timeout);
    item.setAttribute(ATTRIBUTE, 'done');
    item.style.removeProperty(DELAY_PROPERTY);
  };
  const timeout = setTimeout(finish, CLEANUP_DELAY_MS + delay);

  item.addEventListener(
    'transitionend',
    (event) => {
      if (event.target === item && event.propertyName === 'opacity') finish();
    },
    { signal: controller.signal }
  );
  item.style.setProperty(DELAY_PROPERTY, `${delay}ms`);
  item.setAttribute(ATTRIBUTE, 'visible');
}

function init() {
  if (window.Shopify?.designMode) return;
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // The CSS first-screen entrance is for the initial load only. Dropping its class keeps
  // content that is re-rendered later, such as a price after a variant change, from replaying it.
  setTimeout(() => document.body.classList.remove(LOAD_CLASS), LOAD_ENTRANCE_MS);

  // No negative root margin: the last item on the page must still intersect at maximum scroll.
  const observer = new IntersectionObserver((entries) => {
    let step = 0;

    for (const entry of entries) {
      if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;
      observer.unobserve(entry.target);
      reveal(entry.target, step++);
    }
  });

  for (const region of document.querySelectorAll(REGION_SELECTOR)) {
    for (const item of getItems(region)) {
      const rect = item.getBoundingClientRect();
      if (rect.height === 0 || rect.top < window.innerHeight) continue;

      item.setAttribute(ATTRIBUTE, 'pending');
      observer.observe(item);
    }
  }
}

init();
